import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Progress } from "antd";
import styles from "./ProgressRenderBar.module.scss";
import Modal from "Components/UI/Modal";
import Button from "Components/UI/Button";
import { IOpenFileAction } from "Pages/ListagemArquivos";
import { handleOpenForgeViewer } from "Pages/ListagemArquivos/ListagemArquivos.helpers";
import { IUserInforResponse } from "Data/interfaces/Auth/IUserInfoResponse";
import { ITenantData } from "Data/interfaces/Tenant/ITenantData";

interface IProgressRenderBar {
  maxLimits?: number;
  value: number;
  visible?: boolean;
  role?: number;
  onCancel: () => void;
  openFileAction?: IOpenFileAction;
  onConfirmRender?: () => void;
  inFederatedViewer?: boolean;
  userInfo?: IUserInforResponse;
  currentTenant?: ITenantData | null;
  tenantIds?: number[];
  disciplineName?: string | null;
}

const ProgressRenderBar: FC<IProgressRenderBar> = ({
  maxLimits,
  value,
  visible,
  role,
  onCancel,
  openFileAction,
  onConfirmRender,
  inFederatedViewer,
  userInfo,
  currentTenant,
  tenantIds,
  disciplineName,
}) => {
  const { t } = useTranslation('listagemArquivos');
  const navigate = useNavigate();

  const percentOfConstructionsSites = (currentValue: number | undefined, currentLimit: number) => {
    if (currentValue) {
      const value = (currentValue / currentLimit) * 100;
      return Math.floor(value);
    }
  }

  const handleOpenFile = () => {
    if (openFileAction) {
      handleOpenForgeViewer(
        openFileAction?.csId,
        openFileAction?.ConstructionSiteDisciplinesId,
        openFileAction?.record,
        openFileAction?.breadCrumbId,
        openFileAction?.dispatch,
        navigate,
        openFileAction?.constructionSiteApiId,
        openFileAction?.event,
        userInfo,
        currentTenant,
        tenantIds,
        disciplineName,
      )
      onCancel();
    }
    if (!openFileAction && onConfirmRender) {
      onConfirmRender();
      onCancel();
    }
  }

  const renderBarModalButtons = (
    <div className={styles['progress-render-footer-buttons']}>
      <Button
        onClick={onCancel}
        className={styles['progress-render-cancel-button']}
      >
        {t('common:cancel')}
      </Button>
      <Button
        onClick={() => handleOpenFile()}
        type="primary"
        className={styles['progress-render-confirm-button']}
      >
        {inFederatedViewer ? t('renderBar.continue') : t('renderBar.render')}
      </Button>
    </div>
  );

  return (
    <Modal
      title={t('renderBar.title')}
      visible={visible}
      footerButtons={renderBarModalButtons}
      onCancel={onCancel}
      mask={false}
      wrapClassName="ant-modal-mask-render"
    >
      <div className={styles['wrapper']} >
        <div>
          <Progress
            type="circle"
            width={120}
            className={styles['progress-bar']}
            percent={maxLimits
              ? percentOfConstructionsSites(value, maxLimits)
              : 100
            }
            strokeWidth={7}
            strokeColor={"#3547C8"}
            format={() => (
              <div>
                <div className={styles['progress-circle-resources']}>
                  <span>{value}</span>/{maxLimits}
                </div>
                <div className={styles['progress-circle-text']}>
                  {t('renderBar.rendered')}
                </div>
              </div>
            )}
          />
        </div>

        <div className={styles['progress-text']}>
          <p>{role === 1
            ? t('renderBar.yourAccountLimit', { count: maxLimits && (maxLimits - value) })
            : t('renderBar.companyAccountLimit', { count: maxLimits && (maxLimits - value) })
          }</p>
          <p>{t('renderBar.confirmRender')}</p>
        </div>
      </div>
    </Modal>
  )
}

export default ProgressRenderBar;
